<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onMounted } from 'vue';
import { useFitStore } from './stores/fitStore';
import type { SlotType } from '../shared/types';
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts';
import { saveFit } from './composables/useApi';
import AdminPage from './components/admin/AdminPage.vue';
import AppHeader from './components/layout/AppHeader.vue';
import AppFooter from './components/layout/AppFooter.vue';

const isAdminPage = ref(window.location.pathname === '/admin' || window.location.pathname === '/admin/');
import LeftPanel from './components/layout/LeftPanel.vue';
import RightPanel from './components/layout/RightPanel.vue';
import FitTabBar from './components/editor/FitTabBar.vue';
import FittingEditor from './components/editor/FittingEditor.vue';
import BrowseView from './components/browser/BrowseView.vue';
import ImportDialog from './components/modals/ImportDialog.vue';
import ExportDialog from './components/modals/ExportDialog.vue';
import ShareDialog from './components/modals/ShareDialog.vue';
import EveSyncDialog from './components/modals/EveSyncDialog.vue';
import PosBuilder from './components/pos/PosBuilder.vue';

type AppMode = 'ships' | 'pos';

const store = useFitStore();
const posBuilderEnabled = import.meta.env.DEV || import.meta.env.VITE_POS_BUILDER_ENABLED === 'true';
const appMode = ref<AppMode>(
  posBuilderEnabled && (window.location.pathname === '/pos' || window.location.pathname.startsWith('/pos/')) ? 'pos' : 'ships'
);
const posShareToken = computed(() => {
  if (appMode.value !== 'pos') return null;
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments.length > 1 ? decodeURIComponent(segments[1]) : null;
});

// --- Modal visibility ---

const showImportDialog = ref(false);
const showExportDialog = ref(false);
const showShareDialog = ref(false);
const showEveSyncDialog = ref(false);

// --- Keyboard Shortcuts ---

function navigateMode(mode: AppMode): void {
  if (mode === 'pos' && !posBuilderEnabled) return;
  const nextPath = mode === 'pos' ? '/pos' : '/';
  if (window.location.pathname !== nextPath) window.history.pushState({}, '', nextPath);
  appMode.value = mode;
  closeAllModals();
}

function syncLocation(): void {
  isAdminPage.value = window.location.pathname === '/admin' || window.location.pathname === '/admin/';
  appMode.value = posBuilderEnabled && (window.location.pathname === '/pos' || window.location.pathname.startsWith('/pos/')) ? 'pos' : 'ships';
}

function closeAllModals(): void {
  showImportDialog.value = false;
  showExportDialog.value = false;
  showShareDialog.value = false;
  showEveSyncDialog.value = false;
}

const anyModalOpen = computed(() =>
  showImportDialog.value || showExportDialog.value || showShareDialog.value || showEveSyncDialog.value
);

useKeyboardShortcuts({
  onSave: async () => {
    if (appMode.value === 'pos') return;
    const tab = store.activeTab;
    if (!tab?.fitID || store.activeFit?.canEdit === false) return;
    try {
      await saveFit(tab.fitID, store.activeFit?.fitName);
      store.addToast('success', 'Fitting saved');
    } catch {
      // Error shown by useApi
    }
  },
  onImport: () => {
    if (appMode.value === 'ships' && !anyModalOpen.value) showImportDialog.value = true;
  },
  onExport: () => {
    if (appMode.value === 'ships' && !anyModalOpen.value) showExportDialog.value = true;
  },
  onNewFit: () => {
    if (appMode.value !== 'ships') return;
    store.setLeftTab('ships');
    store.addToast('info', 'Select a ship to create a new fit');
  },
  onEscape: () => {
    if (anyModalOpen.value) {
      closeAllModals();
    }
  },
});

// --- Center panel mode ---
// Show browse view when left panel is on "fittings" (public) tab
// Otherwise show the fitting editor
const showBrowseView = computed(() => store.activeLeftTab === 'fittings');

// --- Event handlers ---

function handleSelectShip(typeID: number, typeName: string): void {
  store.createAndLoadFit(typeID, typeName);
}

function handleSelectItem(typeID: number, typeName: string): void {
  const fit = store.activeFit;
  const stats = store.activeStats;
  if (!fit || !stats) {
    store.addToast('info', 'Open a fit first, then select modules');
    return;
  }
  if (fit.canEdit === false) {
    store.addToast('warning', 'This fit is read-only. Copy it to make changes.');
    return;
  }

  const slotTypes: SlotType[] = ['high', 'med', 'low', 'rig'];
  for (const slotType of slotTypes) {
    const slotData = stats.resources.slots[slotType as keyof typeof stats.resources.slots];
    if (slotData && slotData.used < slotData.total) {
      const usedPositions = new Set(
        fit.modules.filter(m => m.slotType === slotType).map(m => m.slotPosition)
      );
      for (let pos = 0; pos < slotData.total; pos++) {
        if (!usedPositions.has(pos)) {
          store.addModuleToFit(typeID, typeName, slotType, pos);
          return;
        }
      }
    }
  }

  store.addModuleToFit(typeID, typeName, 'high', 0);
}

function handleSelectFit(fitID: number): void {
  const tabId = store.addTab(fitID);
  store.loadFit(fitID, tabId);
  // Switch to editor view
  if (store.activeLeftTab === 'fittings') {
    store.setLeftTab('ships');
  }
}

function handleImported(fitID: number): void {
  const tabId = store.addTab(fitID);
  store.loadFit(fitID, tabId);
}

// Handle share from ShipHeader — listen via FittingEditor
function handleShare(): void {
  showShareDialog.value = true;
}

// --- URL-based fit loading ---

onMounted(() => {
  store.init();
  window.addEventListener('popstate', syncLocation);

  // Check for ?fit=N in URL
  const params = new URLSearchParams(window.location.search);
  const fitParam = params.get('fit');
  if (appMode.value === 'ships' && fitParam) {
    const fitID = parseInt(fitParam, 10);
    if (!isNaN(fitID) && fitID > 0) {
      const tabId = store.addTab(fitID);
      store.loadFit(fitID, tabId);
    }
  }
});

onBeforeUnmount(() => window.removeEventListener('popstate', syncLocation));
</script>

<template>
  <AdminPage v-if="isAdminPage" />
  <div v-else class="app">
    <AppHeader
      :mode="appMode" :pos-builder-enabled="posBuilderEnabled"
      @change-mode="navigateMode"
      @import="showImportDialog = true"
      @export="showExportDialog = true"
      @eve-sync="showEveSyncDialog = true"
    />

    <PosBuilder v-if="appMode === 'pos'" :key="posShareToken ?? 'draft'" :share-token="posShareToken" />

    <main v-else class="app-main">
      <LeftPanel
        @select-ship="handleSelectShip"
        @select-item="handleSelectItem"
        @select-fit="handleSelectFit"
      />

      <!-- Center panel: Tab bar always visible, content switches -->
      <div class="center-panel">
        <FitTabBar />
        <BrowseView
          v-if="showBrowseView"
          @select-fit="handleSelectFit"
        />
        <FittingEditor
          v-else
          @share="handleShare"
          @import="showImportDialog = true"
          @export="showExportDialog = true"
        />
      </div>

      <RightPanel />
    </main>

    <AppFooter />

    <!-- Toast Notifications -->
    <TransitionGroup v-if="appMode === 'ships'" name="toast" tag="div" class="toast-container">
      <div
        v-for="toast in store.toasts"
        :key="toast.id"
        class="toast"
        :class="`toast--${toast.type}`"
        @click="store.dismissToast(toast.id)"
      >
        {{ toast.message }}
      </div>
    </TransitionGroup>

    <!-- Modals -->
    <ImportDialog
      :visible="showImportDialog"
      @close="showImportDialog = false"
      @imported="handleImported"
    />

    <ExportDialog
      :visible="showExportDialog"
      @close="showExportDialog = false"
    />

    <ShareDialog
      :visible="showShareDialog"
      :fit-id="store.activeTab?.fitID ?? null"
      :fit-name="store.activeFit?.fitName ?? ''"
      @close="showShareDialog = false"
    />

    <EveSyncDialog
      :visible="showEveSyncDialog"
      @close="showEveSyncDialog = false"
    />
  </div>
</template>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  background:
    radial-gradient(circle at 20% -10%, rgba(0, 212, 255, 0.08), transparent 32%),
    radial-gradient(circle at 85% 8%, rgba(76, 175, 80, 0.045), transparent 28%),
    linear-gradient(180deg, var(--bg-darker), #05070b 100%);
  color: var(--text-primary);
}

.app-main {
  display: grid;
  grid-template-columns: minmax(280px, 340px) minmax(480px, 1fr) minmax(320px, 360px);
  flex: 1;
  min-height: 0;
  gap: 10px;
  padding: 10px;
  overflow: hidden;
}

.center-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background:
    linear-gradient(180deg, rgba(12, 16, 24, 0.88), rgba(8, 11, 16, 0.96)),
    var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--glow-panel);
  overflow: hidden;
}

.toast-container {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toast {
  min-width: 240px;
  max-width: 420px;
  padding: 10px 14px;
  background: var(--bg-raised);
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--accent-blue);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  color: var(--text-primary);
  font-size: 0.86rem;
  cursor: pointer;
}

.toast--success { border-left-color: var(--accent-green); }
.toast--error { border-left-color: var(--accent-red); }
.toast--info { border-left-color: var(--accent-blue); }

.toast-enter-active,
.toast-leave-active {
  transition: all var(--transition-normal);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

@media (max-width: 1100px) {
  .app-main {
    grid-template-columns: minmax(250px, 300px) minmax(420px, 1fr);
  }
}

@media (max-width: 900px) {
  .app {
    height: auto;
    min-height: 100vh;
  }

  .app-main {
    display: flex;
    flex-direction: column;
    overflow: visible;
  }
}
</style>
